import { useEffect, useState } from 'react';
import { api2, ApiError, type CampaignDto, type RhythmDto } from '../api.ts';
import AttachField, { type AttachedFile } from '../components/AttachField.tsx';

/**
 * Campaigns and the rhythms that feed them, on one screen — because a rhythm
 * with no campaign behind it is a chore, and a campaign with nothing to do in
 * it is a poster.
 */
export default function Campaigns(): React.ReactElement {
  const [campaigns, setCampaigns] = useState<CampaignDto[]>([]);
  const [rhythms, setRhythms] = useState<RhythmDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    Promise.all([api2.campaigns(), api2.rhythms()])
      .then(([c, r]) => {
        setCampaigns(c.campaigns);
        setRhythms(r.rhythms);
        setError(null);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load campaigns.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  const mine = rhythms.filter((r) => r.mine);
  const available = rhythms.filter((r) => !r.mine);

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div className="stack-lg">
      <header>
        <h1>Campaigns</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          What the whole of North America is pushing on right now, and your part in it.
        </p>
      </header>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      <section className="stack">
        {campaigns.map((campaign) => (
          <CampaignCard key={campaign.id} campaign={campaign} onChanged={setCampaigns} />
        ))}
        {campaigns.length === 0 && (
          <div className="card empty">
            <p>No campaigns running where you are just now.</p>
          </div>
        )}
      </section>

      <section className="stack">
        <h2>Your rhythms</h2>
        {mine.length === 0 ? (
          <div className="card empty">
            <p>Nothing regular yet — a rhythm is a small thing done often.</p>
          </div>
        ) : (
          mine.map((rhythm) => <RhythmCard key={rhythm.id} rhythm={rhythm} onChanged={setRhythms} />)
        )}
      </section>

      {available.length > 0 && (
        <section className="stack">
          <h2>Take on another</h2>
          {available.map((rhythm) => (
            <RhythmCard key={rhythm.id} rhythm={rhythm} onChanged={setRhythms} />
          ))}
        </section>
      )}
    </div>
  );
}

function CampaignCard({
  campaign,
  onChanged,
}: {
  campaign: CampaignDto;
  onChanged: (next: CampaignDto[]) => void;
}): React.ReactElement {
  const [logging, setLogging] = useState(false);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<{ campaigns: CampaignDto[] }>) => {
    setBusy(true);
    setError(null);
    try {
      onChanged((await fn()).campaigns);
      setLogging(false);
      setLink('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not go through.');
    } finally {
      setBusy(false);
    }
  };

  const goal = campaign.goal;
  // "2 of 5 posts done this week — 3 still to go" rather than a bare number:
  // the prototype's terse labels left out what the remainder was of.
  const remaining = goal ? Math.max(goal.target - campaign.myLogged, 0) : 0;
  const pct = goal && goal.target > 0 ? Math.min(100, Math.round((campaign.myLogged / goal.target) * 100)) : 0;

  return (
    <article className="card stack">
      <div className="row" style={{ gap: 8 }}>
        {campaign.endsOn ? (
          <span className="pill marigold">until {formatDate(campaign.endsOn)}</span>
        ) : (
          <span className="pill">ongoing</span>
        )}
        {campaign.joined && <span className="pill mint">You're in</span>}
        {campaign.interested > 0 && (
          <span className="pill" style={{ marginLeft: 'auto' }}>
            {campaign.interested} taking part
          </span>
        )}
      </div>

      <div>
        <h2>{campaign.name}</h2>
        <p className="soft" style={{ marginTop: 6 }}>
          {campaign.blurb}
        </p>
      </div>

      {goal && (
        <div className="stack" style={{ gap: 6 }}>
          <span className="kicker">Your part</span>
          <p className="small soft">
            {campaign.myLogged} of {goal.target} {goal.unit} {goal.window === 'campaign' ? 'so far' : `this ${goal.window}`}
            {remaining > 0 ? ` — ${remaining} still to go` : ' — done'}
            {goal.window === 'week' ? ', by Sunday night' : ''}.
          </p>
          <div
            style={{ height: 8, borderRadius: 999, background: 'var(--paper)', overflow: 'hidden' }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${campaign.myLogged} of ${goal.target} ${goal.unit}`}
          >
            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--mint)' }} />
          </div>
          {campaign.cityLogged > 0 && (
            <p className="small muted">{campaign.cityLogged} logged across your city.</p>
          )}
        </div>
      )}

      {campaign.detail && <p className="small muted">{campaign.detail}</p>}

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}

      {logging && campaign.log && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="field">
            <label htmlFor={`log-${campaign.id}`}>{campaign.log.prompt}</label>
            <input
              id={`log-${campaign.id}`}
              type="text"
              value={link}
              placeholder="instagram.com/p/…"
              onChange={(e) => setLink(e.target.value)}
            />
          </div>
          <div className="row">
            <button className="btn mint" disabled={busy} onClick={() => void run(() => api2.campaignLog(campaign.id, { link }))}>
              {busy ? 'Saving…' : 'Log it'}
            </button>
            <button className="btn ghost" onClick={() => setLogging(false)}>
              Back
            </button>
          </div>
        </div>
      )}

      <div className="row">
        {campaign.log && !logging && (
          <button className="btn" onClick={() => setLogging(true)} disabled={busy}>
            {campaign.log.cta}
          </button>
        )}
        <button
          className="btn quiet"
          disabled={busy}
          onClick={() => void run(() => api2.campaignInterest(campaign.id, !campaign.joined))}
        >
          {campaign.joined ? "I'm stepping back" : 'Count me in'}
        </button>
      </div>
    </article>
  );
}

function RhythmCard({
  rhythm,
  onChanged,
}: {
  rhythm: RhythmDto;
  onChanged: (next: RhythmDto[]) => void;
}): React.ReactElement {
  const [marking, setMarking] = useState(false);
  const [proof, setProof] = useState('');
  const [photo, setPhoto] = useState<AttachedFile | null>(null);
  const [busy, setBusy] = useState(false);
  const [justTaken, setJustTaken] = useState(false);

  const run = async (fn: () => Promise<{ rhythms: RhythmDto[] }>, taken = false) => {
    setBusy(true);
    try {
      onChanged((await fn()).rhythms);
      setMarking(false);
      setProof('');
      setPhoto(null);
      if (taken) setJustTaken(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="card stack" style={{ gap: 12 }}>
      <div className="row" style={{ gap: 8 }}>
        <span className="pill indigo">{rhythm.cadence === 'daily' ? 'every day' : 'every week'}</span>
        {rhythm.campaignName && <span className="pill">{rhythm.campaignName}</span>}
        {rhythm.streak > 0 && (
          <span className="pill mint" style={{ marginLeft: 'auto' }}>
            {rhythm.cadence === 'daily' ? `Day ${rhythm.streak}` : `${rhythm.streak} weeks`}
          </span>
        )}
      </div>

      <div>
        <h2>{rhythm.title}</h2>
        <p className="soft small" style={{ marginTop: 6 }}>
          {rhythm.blurb}
        </p>
      </div>

      {/* The rhythm points somewhere — put the door right on the card. */}
      {rhythm.linkUrl && (
        <div>
          <a className="btn quiet" href={rhythm.linkUrl} target="_blank" rel="noopener">
            Open the task ↗
          </a>
        </div>
      )}

      {/* Milestone copy, only when it is actually a milestone. */}
      {rhythm.streak > 0 && rhythm.streak >= rhythm.best && rhythm.streak > 2 && (
        <p className="notice mint">
          {rhythm.cadence === 'daily' ? `Day ${rhythm.streak}` : `${rhythm.streak} weeks`} — your longest run yet.
        </p>
      )}

      {justTaken && !rhythm.doneToday && (
        <p className="notice mint">Taken on — it now lives under Your rhythms, above.</p>
      )}

      {marking && (
        <div className="stack" style={{ gap: 10 }}>
          <div className="field">
            <label htmlFor={`proof-${rhythm.id}`}>{rhythm.proofPrompt ?? 'Anything to note?'}</label>
            <input id={`proof-${rhythm.id}`} type="text" value={proof} onChange={(e) => setProof(e.target.value)} />
          </div>
          <AttachField
            label="Add a photo"
            accept="image/*"
            value={photo}
            onChange={setPhoto}
            hint="Optional."
          />
          <div className="row">
            <button
              className="btn mint"
              disabled={busy}
              onClick={() => void run(() => api2.rhythmDone(rhythm.id, { proof, ...(photo ? { fileId: photo.id } : {}) }))}
            >
              {busy ? 'Saving…' : 'Done'}
            </button>
            <button className="btn ghost" onClick={() => setMarking(false)}>
              Back
            </button>
          </div>
        </div>
      )}

      {!marking && (
        <div className="row">
          {rhythm.doneToday ? (
            <span className="pill mint">Done {rhythm.cadence === 'daily' ? 'today' : 'this week'}</span>
          ) : (
            <button className="btn" onClick={() => setMarking(true)} disabled={busy}>
              Mark done
            </button>
          )}
          {rhythm.mine ? (
            <button className="btn ghost" disabled={busy} onClick={() => void run(() => api2.rhythmCommit(rhythm.id, false))}>
              Stop this one
            </button>
          ) : (
            <button className="btn quiet" disabled={busy} onClick={() => void run(() => api2.rhythmCommit(rhythm.id, true), true)}>
              Take this on
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function formatDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
